import { motion } from "framer-motion";

interface ProductFeatureIllustrationProps {
  illustrationKey?: string;
}

function ValidApiIllustration() {
  return (
    <div className="w-full max-w-sm mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-foreground/5 border border-border rounded-xl overflow-hidden shadow-lg"
      >
        {/* Terminal header */}
        <div className="px-4 py-2.5 bg-foreground/5 border-b border-border flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" />
          </div>
          <span className="text-xs text-muted-foreground font-body ml-2">POST /api/validate</span>
        </div>

        {/* Code content */}
        <div className="p-4 font-mono text-xs leading-relaxed">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <span className="text-muted-foreground">{"{"}</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="pl-4"
          >
            <span className="text-coral">"valid"</span>
            <span className="text-muted-foreground">: </span>
            <span className="text-emerald-600 dark:text-emerald-400">true</span>
            <span className="text-muted-foreground">,</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="pl-4"
          >
            <span className="text-coral">"street"</span>
            <span className="text-muted-foreground">: </span>
            <span className="text-primary">"Storgatan 12"</span>
            <span className="text-muted-foreground">,</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="pl-4"
          >
            <span className="text-coral">"municipality"</span>
            <span className="text-muted-foreground">: </span>
            <span className="text-primary">"Stockholm"</span>
            <span className="text-muted-foreground">,</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="pl-4"
          >
            <span className="text-coral">"coords"</span>
            <span className="text-muted-foreground">: </span>
            <span className="text-primary">[59.33, 18.07]</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <span className="text-muted-foreground">{"}"}</span>
          </motion.div>
        </div>

        {/* Status bar */}
        <div className="px-4 py-2 border-t border-border flex items-center gap-2">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 1 }}
            className="w-2 h-2 rounded-full bg-emerald-500"
          />
          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-body">200 OK — 12ms</span>
        </div>
      </motion.div>
    </div>
  );
}

function ValidWebIllustration() {
  return (
    <div className="w-full max-w-sm mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-card border border-border rounded-xl overflow-hidden shadow-lg"
      >
        {/* Browser header */}
        <div className="px-4 py-2.5 bg-secondary border-b border-border flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-foreground/15" />
            <div className="w-2.5 h-2.5 rounded-full bg-foreground/15" />
            <div className="w-2.5 h-2.5 rounded-full bg-foreground/15" />
          </div>
          <div className="flex-1 ml-2 h-5 bg-background rounded-md border border-border flex items-center px-2">
            <span className="text-[10px] text-muted-foreground font-body">valid.geposit.se</span>
          </div>
        </div>

        {/* Search field */}
        <div className="p-4">
          <div className="flex gap-2 mb-4">
            <div className="flex-1 h-9 bg-background rounded-lg border border-border flex items-center px-3">
              <span className="text-sm text-foreground/60 font-body">Sök adress…</span>
            </div>
            <div className="h-9 px-4 bg-coral text-coral-foreground rounded-lg flex items-center text-xs font-body font-semibold">
              Sök
            </div>
          </div>

          {/* Results table */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="grid grid-cols-3 bg-secondary text-xs text-muted-foreground font-body px-3 py-2 border-b border-border">
              <span>Adress</span>
              <span>Kommun</span>
              <span>Status</span>
            </div>
            {[
              { addr: "Storgatan 12", mun: "Stockholm", ok: true },
              { addr: "Kungsgatan 5", mun: "Göteborg", ok: true },
              { addr: "Vägen 99", mun: "—", ok: false },
            ].map((row, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 + i * 0.15 }}
                className="grid grid-cols-3 text-xs font-body px-3 py-2.5 border-b border-border last:border-b-0"
              >
                <span className="text-foreground">{row.addr}</span>
                <span className="text-muted-foreground">{row.mun}</span>
                <span className={row.ok ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}>
                  {row.ok ? "✓ Giltig" : "✗ Ogiltig"}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function AtlasCreateIllustration() {
  const zones = [
    // Teal — left column
    { x: 8, y: 8, w: 122, h: 112, rx: 8, fill: "hsl(186 100% 18.4% / 0.14)", stroke: "hsl(186 100% 18.4% / 0.5)", postcode: "611 00", delay: 0.3 },
    { x: 8, y: 124, w: 122, h: 112, rx: 8, fill: "hsl(186 100% 18.4% / 0.10)", stroke: "hsl(186 100% 18.4% / 0.4)", postcode: "611 07", delay: 0.4 },
    // Coral — middle column
    { x: 134, y: 8, w: 130, h: 112, rx: 8, fill: "hsl(0 100% 70% / 0.10)", stroke: "hsl(0 100% 70% / 0.35)", postcode: "612 10", delay: 0.5 },
    { x: 134, y: 124, w: 130, h: 112, rx: 8, fill: "hsl(0 100% 70% / 0.07)", stroke: "hsl(0 100% 70% / 0.28)", postcode: "612 15", delay: 0.6 },
    // Turkos — right column
    { x: 268, y: 8, w: 124, h: 112, rx: 8, fill: "hsl(181 100% 33% / 0.10)", stroke: "hsl(181 100% 33% / 0.35)", postcode: "613 20", delay: 0.7 },
    { x: 268, y: 124, w: 124, h: 112, rx: 8, fill: "hsl(181 100% 33% / 0.07)", stroke: "hsl(181 100% 33% / 0.25)", postcode: "613 25", delay: 0.8 },
  ];

  const legend = [
    { color: "hsl(186 100% 18.4%)", label: "Område 1" },
    { color: "hsl(0 100% 70%)", label: "Område 2" },
    { color: "hsl(181 100% 33%)", label: "Område 3" },
  ];

  return (
    <div className="w-full max-w-sm mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative bg-primary/5 border border-border rounded-xl overflow-hidden shadow-lg"
      >
        <svg viewBox="0 0 400 260" className="w-full" fill="none">
          {/* Subtle grid lines */}
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={`gv-${i}`} x1={i * 50 + 25} y1={0} x2={i * 50 + 25} y2={260} stroke="hsl(186 100% 18.4% / 0.06)" strokeWidth={0.5} />
          ))}
          {Array.from({ length: 6 }).map((_, i) => (
            <line key={`gh-${i}`} x1={0} y1={i * 45 + 10} x2={400} y2={i * 45 + 10} stroke="hsl(186 100% 18.4% / 0.06)" strokeWidth={0.5} />
          ))}

          {/* Zone rectangles */}
          {zones.map((zone, i) => (
            <motion.g key={`zone-${i}`}>
              <motion.rect
                x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx={zone.rx}
                fill={zone.fill} stroke={zone.stroke} strokeWidth={1.5}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: zone.delay }}
              />
              <motion.text
                x={zone.x + zone.w / 2} y={zone.y + zone.h / 2 + 4}
                textAnchor="middle" fontSize="10" fontWeight="500"
                fill={zone.stroke}
                initial={{ opacity: 0 }} animate={{ opacity: 0.8 }}
                transition={{ delay: zone.delay + 0.3 }}
              >
                {zone.postcode}
              </motion.text>
            </motion.g>
          ))}
        </svg>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 py-2 border-t border-border bg-card/50">
          {legend.map((item, i) => (
            <motion.div key={i} className="flex items-center gap-1.5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 + i * 0.1 }}>
              <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: item.color }} />
              <span className="text-[10px] text-muted-foreground font-body">{item.label}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function AtlasCalculateIllustration() {
  // Simple rect zones as subtle background
  const bgZones = [
    { x: 8, y: 8, w: 100, h: 95, rx: 6, fill: "hsl(186 100% 18.4% / 0.06)", stroke: "hsl(186 100% 18.4% / 0.12)", delay: 0.2 },
    { x: 8, y: 107, w: 100, h: 95, rx: 6, fill: "hsl(186 100% 18.4% / 0.04)", stroke: "hsl(186 100% 18.4% / 0.08)", delay: 0.25 },
    { x: 112, y: 8, w: 110, h: 95, rx: 6, fill: "hsl(181 100% 33% / 0.04)", stroke: "hsl(181 100% 33% / 0.08)", delay: 0.3 },
    { x: 112, y: 107, w: 110, h: 95, rx: 6, fill: "hsl(181 100% 33% / 0.03)", stroke: "hsl(181 100% 33% / 0.06)", delay: 0.32 },
    { x: 226, y: 8, w: 106, h: 194, rx: 6, fill: "hsl(0 100% 70% / 0.03)", stroke: "hsl(0 100% 70% / 0.06)", delay: 0.35 },
  ];

  return (
    <div className="w-full max-w-sm mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative bg-primary/5 border border-border rounded-xl overflow-hidden shadow-lg aspect-[4/3] p-6"
      >
        <svg viewBox="0 0 340 230" className="w-full h-full" fill="none">
          {/* Background zone rects */}
          {bgZones.map((zone, i) => (
            <motion.rect
              key={`bg-${i}`}
              x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx={zone.rx}
              fill={zone.fill} stroke={zone.stroke} strokeWidth={1}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: zone.delay }}
            />
          ))}

          {/* Route line */}
          <motion.path
            d="M40 180 C80 180, 100 100, 140 100 S200 40, 240 80 S300 60, 300 60"
            stroke="hsl(186 100% 18.4% / 0.6)"
            strokeWidth={2.5}
            strokeDasharray="6 4"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.5, delay: 0.5 }}
          />

          {/* Points */}
          {[
            { cx: 40, cy: 180, label: "A", delay: 0.7 },
            { cx: 140, cy: 100, label: "B", delay: 1.1 },
            { cx: 240, cy: 80, label: "C", delay: 1.5 },
            { cx: 300, cy: 60, label: "D", delay: 1.8 },
          ].map((pt, i) => (
            <motion.g key={i}>
              <motion.circle
                cx={pt.cx} cy={pt.cy} r={14}
                fill="hsl(186 100% 18.4% / 0.1)"
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ delay: pt.delay }}
                style={{ transformOrigin: `${pt.cx}px ${pt.cy}px` }}
              />
              <motion.circle
                cx={pt.cx} cy={pt.cy} r={6}
                fill="hsl(186 100% 18.4%)"
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ type: "spring", delay: pt.delay + 0.1 }}
                style={{ transformOrigin: `${pt.cx}px ${pt.cy}px` }}
              />
              <motion.text
                x={pt.cx} y={pt.cy + 28}
                textAnchor="middle"
                className="fill-foreground text-xs font-body font-semibold"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ delay: pt.delay + 0.2 }}
              >
                {pt.label}
              </motion.text>
            </motion.g>
          ))}

          {/* Distance labels */}
          <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0 }}>
            <rect x={60} y={125} width={60} height={22} rx={4} fill="hsl(var(--card))" stroke="hsl(var(--border))" />
            <text x={90} y={140} textAnchor="middle" className="fill-muted-foreground text-[10px] font-body">12.3 km</text>
          </motion.g>
          <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }}>
            <rect x={170} y={65} width={55} height={22} rx={4} fill="hsl(var(--card))" stroke="hsl(var(--border))" />
            <text x={197} y={80} textAnchor="middle" className="fill-muted-foreground text-[10px] font-body">8.7 km</text>
          </motion.g>
        </svg>
      </motion.div>
    </div>
  );
}

function AtlasIntegrateIllustration() {
  const zones = [
    { x: 8, y: 8, w: 88, h: 48, rx: 5, fill: "hsl(186 100% 18.4% / 0.12)", stroke: "hsl(186 100% 18.4% / 0.35)", postcode: "114 51", delay: 0.3 },
    { x: 100, y: 8, w: 92, h: 48, rx: 5, fill: "hsl(0 100% 70% / 0.10)", stroke: "hsl(0 100% 70% / 0.28)", postcode: "115 21", delay: 0.4 },
    { x: 8, y: 60, w: 88, h: 48, rx: 5, fill: "hsl(181 100% 33% / 0.10)", stroke: "hsl(181 100% 33% / 0.3)", postcode: "116 40", delay: 0.5 },
    { x: 100, y: 60, w: 92, h: 48, rx: 5, fill: "hsl(186 100% 18.4% / 0.06)", stroke: "hsl(186 100% 18.4% / 0.18)", postcode: "116 45", delay: 0.55 },
  ];

  const pins = [
    { cx: 52, cy: 38, color: "hsl(186 100% 18.4%)", delay: 0.8 },
    { cx: 146, cy: 90, color: "hsl(181 100% 33%)", delay: 1.0 },
  ];

  return (
    <div className="w-full max-w-sm mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-card border border-border rounded-xl overflow-hidden shadow-lg"
      >
        {/* iFrame preview header */}
        <div className="px-4 py-2.5 bg-secondary border-b border-border flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-foreground/15" />
            <div className="w-2.5 h-2.5 rounded-full bg-foreground/15" />
            <div className="w-2.5 h-2.5 rounded-full bg-foreground/15" />
          </div>
          <code className="text-[10px] text-muted-foreground font-mono ml-2">{"<iframe src=\"atlas.geposit.se\">"}</code>
        </div>
        <div className="p-4">
          <div className="bg-primary/5 rounded-lg border border-border h-32 flex items-center justify-center relative overflow-hidden">
            <svg viewBox="0 0 200 120" className="w-full h-full absolute inset-0" fill="none">
              {/* Zone rectangles */}
              {zones.map((zone, i) => (
                <motion.g key={`zone-${i}`}>
                  <motion.rect
                    x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx={zone.rx}
                    fill={zone.fill} stroke={zone.stroke} strokeWidth={1}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ delay: zone.delay }}
                  />
                  <motion.text
                    x={zone.x + zone.w / 2} y={zone.y + 18}
                    textAnchor="middle" fontSize="6"
                    fill={zone.stroke}
                    initial={{ opacity: 0 }} animate={{ opacity: 0.7 }}
                    transition={{ delay: zone.delay + 0.2 }}
                  >
                    {zone.postcode}
                  </motion.text>
                </motion.g>
              ))}

              {/* Map pins */}
              {pins.map((pin, i) => (
                <motion.circle
                  key={`pin-${i}`}
                  cx={pin.cx} cy={pin.cy} r={4}
                  fill={pin.color} stroke="hsl(0 0% 100%)" strokeWidth={1}
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ delay: pin.delay, type: "spring" }}
                  style={{ transformOrigin: `${pin.cx}px ${pin.cy}px` }}
                />
              ))}
            </svg>
          </div>

          {/* API connection labels */}
          <div className="mt-3 flex gap-2">
            {["REST API", "iFrame", "Webhook"].map((label, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 + i * 0.1 }}
                className="flex-1 text-center py-1.5 rounded-md bg-secondary border border-border text-[10px] text-muted-foreground font-body"
              >
                {label}
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function AdresstwattAnalyzeIllustration() {
  const rows = [
    { text: "Storgatan 12, Stocholm", status: "error" },
    { text: "Kungsgtn 5, Gbg", status: "warning" },
    { text: "Drottningg. 8, Malmö", status: "warning" },
    { text: "Sveavägen 10, Stockholm", status: "ok" },
  ];

  return (
    <div className="w-full max-w-sm mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-card border border-border rounded-xl overflow-hidden shadow-lg"
      >
        <div className="px-4 py-3 border-b border-border">
          <span className="text-sm font-body font-semibold text-foreground">Analysresultat</span>
          <span className="ml-2 text-xs text-muted-foreground font-body">4 adresser</span>
        </div>
        <div className="divide-y divide-border">
          {rows.map((row, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.12 }}
              className="px-4 py-2.5 flex items-center gap-3"
            >
              <div
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  row.status === "error" ? "bg-red-500" : row.status === "warning" ? "bg-yellow-500" : "bg-emerald-500"
                }`}
              />
              <span className="text-sm font-body text-foreground flex-1">{row.text}</span>
              <span
                className={`text-xs font-body ${
                  row.status === "error" ? "text-red-500" : row.status === "warning" ? "text-yellow-600 dark:text-yellow-400" : "text-emerald-600 dark:text-emerald-400"
                }`}
              >
                {row.status === "error" ? "Ogiltig" : row.status === "warning" ? "Osäker" : "OK"}
              </span>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="px-4 py-3 border-t border-border flex gap-3 text-xs text-muted-foreground font-body"
        >
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> 1 ogiltig</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" /> 2 osäkra</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> 1 OK</span>
        </motion.div>
      </motion.div>
    </div>
  );
}

function AdresstwattCorrectIllustration() {
  const rows = [
    { before: "Stocholm", after: "Stockholm" },
    { before: "Kungsgtn", after: "Kungsgatan" },
    { before: "Drottningg.", after: "Drottninggatan" },
  ];

  return (
    <div className="w-full max-w-sm mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-card border border-border rounded-xl overflow-hidden shadow-lg"
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <span className="text-sm font-body font-semibold text-foreground">Korrigerade adresser</span>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="text-xs text-emerald-600 dark:text-emerald-400 font-body"
          >
            ✓ 3 korrigerade
          </motion.span>
        </div>
        <div className="divide-y divide-border">
          {rows.map((row, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.15 }}
              className="px-4 py-3 flex items-center gap-3"
            >
              <span className="text-sm text-red-400 line-through font-body min-w-[100px]">{row.before}</span>
              <svg className="w-4 h-4 text-muted-foreground flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 + i * 0.15 }}
                className="text-sm text-emerald-600 dark:text-emerald-400 font-body font-semibold"
              >
                {row.after}
              </motion.span>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="h-1 bg-gradient-to-r from-coral to-emerald-500 origin-left"
        />
      </motion.div>
    </div>
  );
}

export function ProductFeatureIllustration({ illustrationKey }: ProductFeatureIllustrationProps) {
  if (!illustrationKey) return null;

  switch (illustrationKey) {
    case "valid-api":
      return <ValidApiIllustration />;
    case "valid-web":
      return <ValidWebIllustration />;
    case "atlas-create":
      return <AtlasCreateIllustration />;
    case "atlas-calculate":
      return <AtlasCalculateIllustration />;
    case "atlas-integrate":
      return <AtlasIntegrateIllustration />;
    case "adresstwatt-analyze":
      return <AdresstwattAnalyzeIllustration />;
    case "adresstwatt-correct":
      return <AdresstwattCorrectIllustration />;
    default:
      return null;
  }
}
